import React, { useState, useEffect } from 'react';
import { BarChart3, RefreshCw, Download, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('qantri_admin_token');

  const fetchCategorySummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/products/category-summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Errore nel caricamento delle categorie');
      }
      
      const data = await response.json();
      setCategoryData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCategories = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/products/sync-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Errore durante l\'esportazione');
      }
      
      const data = await response.json();
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `categories_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSyncResult({
        success: true,
        message: `Esportate ${data.total_products} categorie prodotti`
      });
    } catch (err) {
      setSyncResult({
        success: false,
        message: err.message
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchCategorySummary();
  }, []);

  const getCategoryColor = (count, maxCount) => {
    const percentage = (count / maxCount) * 100;
    if (percentage > 20) return 'bg-emerald-500';
    if (percentage > 10) return 'bg-emerald-400';
    if (percentage > 5) return 'bg-emerald-300';
    return 'bg-emerald-200';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Categorie</h1>
          <p className="text-gray-600 mt-1">Visualizza e gestisci le categorie dei prodotti</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchCategorySummary}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            <button
              onClick={exportCategories}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${syncing ? 'animate-bounce' : ''}`} />
              Esporta Categorie
            </button>
          </div>
        </div>

        {/* Sync Result */}
        {syncResult && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            syncResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {syncResult.success ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{syncResult.message}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* Category Data */}
        {!loading && categoryData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Totale Prodotti</p>
                    <p className="text-2xl font-bold text-gray-900">{categoryData.total_products?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Categorie Attive</p>
                    <p className="text-2xl font-bold text-gray-900">{categoryData.categories?.length || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Media per Categoria</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {Math.round(categoryData.total_products / (categoryData.categories?.length || 1))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Category List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">Distribuzione per Categoria</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {categoryData.categories?.map((cat, index) => {
                  const maxCount = categoryData.categories[0]?.count || 1;
                  const percentage = ((cat.count / categoryData.total_products) * 100).toFixed(1);
                  
                  return (
                    <div key={cat.category} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                          <span className="font-medium text-gray-900 capitalize">{cat.category}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">{percentage}%</span>
                          <span className="font-semibold text-gray-900 w-20 text-right">
                            {cat.count.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-9">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getCategoryColor(cat.count, maxCount)} transition-all duration-500`}
                            style={{ width: `${(cat.count / maxCount) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="font-medium text-blue-900 mb-2">Sincronizzazione con Produzione</h3>
              <p className="text-sm text-blue-800">
                Dopo aver corretto le categorie, usa "Esporta Categorie" per scaricare un file JSON. 
                Questo file può essere importato nel database di produzione dopo un re-deploy per 
                sincronizzare le modifiche alle categorie.
              </p>
            </div>
          </>
        )}
      </div>
  );
};

export default AdminCategories;
